// data-fit LLC · DecisionFlow · EXECUTE Stage
// CalculationRunInitiator.jsx · DDL: R5 · API: v1.27 · S247 Phase 10
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: confirm_final gate, run_status never sent, fiscal_period_id integer
// S572-T1d: scenario_set_id removed from base calc run — scenario exposure is post-calculation Finance step
// PATCH-S92-04: run id read as calculation_run_id || id (DDL PK: calculation_run_id)
// PATCH-S206-01: scenario_set_id added to POST body (422 confirmed missing field); fiscalPeriodLabel prop added for display
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
// S247 Phase 10:
//   - OI-AMENDMENT-UI-FINAL-RUN-BANNER: AMENDMENT_REAPPROVAL_PENDING handled as dedicated banner
//   - OI-AMENDMENT-UI-PREVIEW-RERUN: Preview Rerun section added (POST /calculation-runs/preview, 60s cooldown)
//   - OI-AMENDMENT-UI-DELTA-PANEL: AmendmentDeltaPanel mounted after preview rerun completes
//   - comp_admin gating: preview rerun + delta hidden from other roles

import React, { useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'
import AmendmentDeltaPanel from '../admin/AmendmentDeltaPanel.jsx'

const RUN_TYPES = ['preview', 'final']
const PREVIEW_RERUN_COOLDOWN_MS = 60_000

function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected calculation run error.'

  if (error.code === 'CALCULATION_RUN_NOT_FOUND') {
    return 'Calculation run was not found.'
  }

  if (error.code === 'INVALID_STATUS_TRANSITION') {
    return error.recovery_action || 'The calculation run transition is invalid.'
  }

  if (error.code === 'AUTH_INSUFFICIENT_ROLE') {
    return 'You do not have the comp_admin role required to create runs.'
  }

  if (error.code === 'FEATURE_UNAVAILABLE') {
    return 'Calculation runs are unavailable for the current tenant tier.'
  }

  // AMENDMENT_REAPPROVAL_PENDING is handled as a structured banner — not a plain error string.
  // Callers should check payload.error.code before calling this function.
  return error.recovery_action || error.message || 'Unable to create run.'
}

// Extracts stage list from AMENDMENT_REAPPROVAL_PENDING error payload.
// API returns stages_requiring_reapproval: [{ stage_id, stage_label }] or similar.
function extractPendingStages(payload) {
  const err = payload?.error
  if (!err) return []
  return (
    err.stages_requiring_reapproval ??
    err.pending_stages ??
    err.detail?.stages_requiring_reapproval ??
    []
  )
}

// -----------------------------------------------------------------------------
// Amendment Reapproval Pending Banner — OI-AMENDMENT-UI-FINAL-RUN-BANNER
// -----------------------------------------------------------------------------
function AmendmentReapprovalBanner({ stages, onDismiss }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-5 py-4 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
            ⚠ Final Run Blocked — Amendment Reapproval Pending
          </p>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            One or more wizard stages were affected by committed amendments and require explicit reapproval before a final run can proceed.
          </p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-warning))]/20"
            aria-label="Dismiss"
          >
            ✕
          </button>
        )}
      </div>

      {stages.length > 0 && (
        <div>
          <p className="mb-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">
            Stages requiring reapproval
          </p>
          <ul className="space-y-1">
            {stages.map((s, i) => (
              <li
                key={s.stage_id ?? i}
                className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))]"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(var(--color-warning))]/20 text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
                  {s.stage_id ?? i + 1}
                </span>
                {s.stage_label ?? s.label ?? `Stage ${s.stage_id ?? i + 1}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        Go to each flagged stage and click <strong>Reapprove</strong> to clear it. All stages must be cleared before the final run can proceed.
      </p>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Plan Approval Guidance Banner — OI-CALC-422-PLAN-ASSIGNMENT-GUIDANCE
// Shown when POST /calculation-runs returns PLAN_VERSION_UNAPPROVED (422).
// Lists unapproved plans from error.detail.unapproved_plans and directs CA
// to Compensation Policies → Approvals tab to approve before retrying.
// -----------------------------------------------------------------------------
function PlanApprovalGuidanceBanner({ unapprovedPlans, onDismiss }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-5 py-4 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
            ⚠ Calculation Blocked — Plans Not Approved
          </p>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            All compensation plans for this period must be approved before a calculation run can proceed.
          </p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-warning))]/20"
            aria-label="Dismiss"
          >
            ✕
          </button>
        )}
      </div>

      {unapprovedPlans.length > 0 && (
        <div>
          <p className="mb-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">
            Plans requiring approval
          </p>
          <ul className="space-y-1">
            {unapprovedPlans.map((p, i) => (
              <li
                key={p.plan_id ?? i}
                className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))]"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(var(--color-warning))]/20 text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
                  !
                </span>
                <span className="font-mono text-tok-xs">{p.plan_code ?? p.plan_id}</span>
                <span className="text-[rgb(var(--color-text-muted))]">— {p.plan_status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        Go to <strong>Compensation Policies → Approvals</strong> and approve all pending plans for this period, then retry the calculation run.
      </p>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Preview Rerun section — OI-AMENDMENT-UI-PREVIEW-RERUN
// Separate from the regular calculation run. Calls POST /calculation-runs/preview.
// Only shown to comp_admin. Enabled when committedAmendmentsExist and no active run.
// 60s cooldown visual enforced client-side.
// -----------------------------------------------------------------------------
function PreviewRerunSection({ fiscalPeriodId, scenarioSetId, onRerunComplete }) {
  const [submitting, setSubmitting]           = useState(false)
  const [error, setError]                     = useState(null)
  const [success, setSuccess]                 = useState(null)
  const [cooldownRemaining, setCooldownRemaining] = useState(0)
  const cooldownRef                           = useRef(null)

  const startCooldown = () => {
    setCooldownRemaining(PREVIEW_RERUN_COOLDOWN_MS / 1000)
    cooldownRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  const handlePreviewRerun = async () => {
    setError(null); setSuccess(null)
    if (!fiscalPeriodId) { setError('No active fiscal period.'); return }
    // S572-T1d: scenarioSetId gate removed from preview rerun — not required for base calc.

    setSubmitting(true)
    try {
      const res = await fetch('/api/v1/calculation-runs/preview', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // S572-T1d: scenario_set_id omitted — preview rerun is base calc only
        body: JSON.stringify({
          fiscal_period_id: Number(fiscalPeriodId),
        }),
      })
      const payload = await res.json().catch(() => ({}))

      if (!res.ok) {
        const err = payload?.error ?? payload?.detail?.error
        if (err?.code === 'AMENDMENT_REAPPROVAL_PENDING') {
          setError('Reapproval is still pending on one or more stages. Reapprove all flagged stages before re-running preview.')
        } else if (err?.code === 'RERUN_COOLDOWN_ACTIVE' || err?.code === 'PREVIEW_RERUN_COOLDOWN') {
          setError(err.message || 'Preview rerun is on cooldown. Please wait before retrying.')
        } else {
          setError(err?.message || err?.recovery_action || `Error ${res.status}`)
        }
        return
      }

      const runId = payload?.data?.calculation_run_id ?? payload?.calculation_run_id ?? payload?.id ?? null
      startCooldown()
      setSuccess('Preview rerun started successfully.')
      if (onRerunComplete) onRerunComplete(runId)
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setSubmitting(false)
    }
  }

  const isDisabled = submitting || cooldownRemaining > 0

  return (
    <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 p-5 flex flex-col gap-3">
      <div>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-warning))] mb-1">
          Amendment Rerun
        </p>
        <h3 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Preview Rerun</h3>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">
          Run a new preview calculation to reflect committed amendments. Review the delta before reapproving stages.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      )}
      {success && !error && (
        <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
          {success}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handlePreviewRerun}
          disabled={isDisabled}
          className="rounded-xl bg-[rgb(var(--color-warning))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting
            ? 'Starting rerun…'
            : cooldownRemaining > 0
            ? `Cooldown (${cooldownRemaining}s)`
            : 'Run Preview'}
        </button>
        {cooldownRemaining > 0 && (
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Next rerun available in {cooldownRemaining}s
          </p>
        )}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Main export
// -----------------------------------------------------------------------------
export default function CalculationRunInitiator({
  fiscalPeriodId,
  fiscalPeriodLabel,
  scenarioSetId,
  setRunId,
  onPreviewRerunComplete,
}) {
  const { user } = useAuth()
  const isCompAdmin = user?.user_type === 'comp_admin'

  const [runType, setRunType]               = useState('preview')
  const [runLabel, setRunLabel]             = useState('')
  const [confirmFinal, setConfirmFinal]     = useState(false)
  const [showFinalConfirm, setShowFinalConfirm] = useState(false)
  const [createdRun, setCreatedRun]         = useState(null)
  const [loading, setLoading]               = useState(false)
  const [errorMessage, setErrorMessage]     = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Amendment reapproval banner state
  const [reapprovalBanner, setReapprovalBanner] = useState(null) // { stages: [] }
  // OI-CALC-422-PLAN-ASSIGNMENT-GUIDANCE: plan approval guidance banner
  const [planApprovalBanner, setPlanApprovalBanner] = useState(null) // { unapprovedPlans: [] }

  // Preview rerun state — tracks the latest preview-rerun run id for delta panel
  const [previewRerunId, setPreviewRerunId]   = useState(null)
  const [showDeltaPanel, setShowDeltaPanel]   = useState(false)

  const periodId = useMemo(() => {
    const parsed = Number(fiscalPeriodId)
    return Number.isInteger(parsed) ? parsed : null
  }, [fiscalPeriodId])

  const submitRun = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    setReapprovalBanner(null)
    setPlanApprovalBanner(null)

    if (!periodId) {
      setErrorMessage('A valid integer fiscal_period_id is required.')
      return
    }

    // S572-T1d: scenario_set_id gate removed — scenario exposure is post-calculation.
    // Calculation run proceeds without a scenario set. Finance triggers exposure separately.

    if (!RUN_TYPES.includes(runType)) {
      setErrorMessage("run_type must be exactly 'preview' or 'final'.")
      return
    }

    if (runType === 'final' && !confirmFinal) {
      setShowFinalConfirm(true)
      setErrorMessage('Final runs require explicit confirmation.')
      return
    }

    // S572-T1d: scenario_set_id omitted from base calculation run body.
    // Scenario exposure is Finance-triggered post-calculation via POST /scenario-exposure/compute.
    const body = {
      fiscal_period_id: periodId,
      run_type: runType,
    }

    // S587: optional run label — trim client-side before send; omit if empty
    const trimmedLabel = runLabel.trim()
    if (trimmedLabel) {
      body.run_label = trimmedLabel
    }

    if (runType === 'final') {
      body.confirm_final = true
    }

    setLoading(true)

    try {
      const response = await fetch('/api/v1/calculation-runs', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        // OI-AMENDMENT-UI-FINAL-RUN-BANNER: intercept AMENDMENT_REAPPROVAL_PENDING
        if (payload?.error?.code === 'AMENDMENT_REAPPROVAL_PENDING') {
          setReapprovalBanner({ stages: extractPendingStages(payload) })
          return
        }
        // OI-CALC-422-PLAN-ASSIGNMENT-GUIDANCE: intercept PLAN_VERSION_UNAPPROVED
        if (payload?.error?.code === 'PLAN_VERSION_UNAPPROVED') {
          setPlanApprovalBanner({
            unapprovedPlans: payload?.error?.detail?.unapproved_plans ?? [],
          })
          return
        }
        throw new Error(getErrorMessage(payload))
      }

      const run = payload?.data || payload
      setCreatedRun(run)

      // PATCH-S92-04: DDL PK is calculation_run_id — fall back to id for API shape variance
      const resolvedRunId = run?.calculation_run_id || run?.id
      if (resolvedRunId) {
        setRunId(resolvedRunId)
      }

      setSuccessMessage(
        `Calculation run created. Engine-owned status: ${run?.run_status || 'pending'}.`,
      )
      setShowFinalConfirm(false)
    } catch (error) {
      setErrorMessage(error.message || 'Unable to create calculation run.')
    } finally {
      setLoading(false)
    }
  }

  const goToMonitor = () => {
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { screen: SCREEN_ORDER[2] },
      }),
    )
  }

  const handlePreviewRerunComplete = (runId) => {
    setPreviewRerunId(runId)
    setShowDeltaPanel(true)
    if (onPreviewRerunComplete) onPreviewRerunComplete(runId)
  }

  return (
    <section className="space-y-6">
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">EXEC-02</p>
        <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Calculation Run Initiator</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">Create preview or final calculation runs for this fiscal period.</p>
      </header>

      {/* OI-AMENDMENT-UI-FINAL-RUN-BANNER */}
      {reapprovalBanner && (
        <AmendmentReapprovalBanner
          stages={reapprovalBanner.stages}
          onDismiss={() => setReapprovalBanner(null)}
        />
      )}

      {/* OI-CALC-422-PLAN-ASSIGNMENT-GUIDANCE */}
      {planApprovalBanner && (
        <PlanApprovalGuidanceBanner
          unapprovedPlans={planApprovalBanner.unapprovedPlans}
          onDismiss={() => setPlanApprovalBanner(null)}
        />
      )}

      {errorMessage && (
        <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">{errorMessage}</div>
      )}
      {successMessage && (
        <div className="rounded-md bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-4 py-3 text-tok-sm">{successMessage}</div>
      )}

      {/* Standard run form */}
      <div className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6 space-y-4">
        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Fiscal Period
          <input value={fiscalPeriodLabel || periodId || ''} readOnly className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]" />
        </label>

        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Run Type
          <select
            value={runType}
            onChange={(event) => {
              setRunType(event.target.value)
              setConfirmFinal(false)
              setShowFinalConfirm(false)
            }}
            className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none"
          >
            <option value="preview">preview</option>
            <option value="final">final</option>
          </select>
        </label>

        <div>
          <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
            Run Label
            <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(optional)</span>
            <input
              value={runLabel}
              onChange={(e) => setRunLabel(e.target.value)}
              maxLength={120}
              placeholder="e.g. Pre-close sanity check, with retro adj applied"
              className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none"
            />
          </label>
          <p className="mt-1 text-right text-tok-xs text-[rgb(var(--color-text-muted))]">
            {runLabel.length}/120
          </p>
        </div>

        {!scenarioSetId && (
          <div className="rounded-md bg-[rgb(var(--color-primary))]/8 border border-[rgb(var(--color-primary))]/20 px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Run will calculate base actuals. Add a scenario set later to model P50/P75/P90 exposure.
          </div>
        )}

        {runType === 'final' && (
          <div className="rounded-md bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 p-4 space-y-2">
            <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-warning))] font-medium">
              <input
                type="checkbox"
                checked={confirmFinal}
                onChange={(event) => setConfirmFinal(event.target.checked)}
                className="h-4 w-4 rounded border-[rgb(var(--color-border))]"
              />
              I confirm this is a final calculation run.
            </label>
            {showFinalConfirm && (
              <p className="text-tok-xs text-[rgb(var(--color-warning))]">
                Final runs require confirm_final=true before submission.
              </p>
            )}
          </div>
        )}


        <button
          type="button"
          onClick={submitRun}
          disabled={loading}
          className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          {loading ? 'Creating run…' : 'Create calculation run'}
        </button>
      </div>

      {createdRun && (
        <article className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Created Run</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-tok-sm mb-4">
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Run ID</dt>
            {/* PATCH-S92-04: DDL PK is calculation_run_id */}
            <dd className="text-[rgb(var(--color-text))]">{createdRun.calculation_run_id || createdRun.id || '—'}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Run Type</dt>
            <dd className="text-[rgb(var(--color-text))]">{createdRun.run_type || runType}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Run Status</dt>
            <dd className="text-[rgb(var(--color-text))]">{createdRun.run_status || 'pending'}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Locked</dt>
            <dd className="text-[rgb(var(--color-text))]">{createdRun.is_locked ? 'Yes' : 'No'}</dd>
            {createdRun.run_label && (
              <>
                <dt className="font-medium text-[rgb(var(--color-text-muted))]">Label</dt>
                <dd className="text-[rgb(var(--color-text))]">{createdRun.run_label}</dd>
              </>
            )}
          </dl>
          <button
            type="button"
            onClick={goToMonitor}
            className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))]"
          >
            Monitor this run
          </button>
        </article>
      )}

      {/* OI-AMENDMENT-UI-PREVIEW-RERUN — comp_admin only */}
      {isCompAdmin && (
        <PreviewRerunSection
          fiscalPeriodId={periodId}
          scenarioSetId={scenarioSetId}
          onRerunComplete={handlePreviewRerunComplete}
        />
      )}

      {/* OI-AMENDMENT-UI-DELTA-PANEL — shown after preview rerun completes, comp_admin only */}
      {isCompAdmin && showDeltaPanel && (
        <AmendmentDeltaPanel
          fiscalPeriodId={periodId}
          previewRunId={previewRerunId}
          onDismiss={() => setShowDeltaPanel(false)}
        />
      )}
    </section>
  )
}
